/* Controllo di liquidità — prima di impegnare capitale su una coppia DEX.
 *
 * Il saldo di gas sta ACCANTO al campo che lo consuma, non in un riquadro in
 * fondo: se la ragione per cui non si può operare è in cima e il pulsante è in
 * fondo, chi preme non sa perché non succede nulla. È il motivo per cui questo
 * blocco usa `CampoDatoVivo` e non un campo qualsiasi.
 *
 * La curva di profondità è dietro un pulsante suo, con il costo scritto: il
 * backend fa fino a sette preventivi in più, e farli a ogni battuta di tasto
 * significherebbe farsi limitare dal provider proprio quando serve il numero.
 */

import { useState } from 'react';
import {
  Avvertenza,
  Distintivo,
  Pulsante,
  Scheda,
  Selettore,
  type VoceSelettore,
} from '../../componenti';
import { CampoDatoVivo, type StatoDatoVivo } from '../../componenti/CampoDatoVivo';
import { Campo } from '../../componenti/Modulo';
import { GraficoProfondita } from '../../componenti/Grafici';
import { Rotella } from '../../componenti/Stati';
import { tokens } from '../../api/risorse';
import { ErroreApi } from '../../api/client';
import { daQuanto, num, ora, valuta } from '../../lib/formato';
import { importoToken } from './dati';
import type { Dex, EsitoLiquidita, PreventivoDex, SaldiCatena } from '../../api/tipi';

const NOME_PROVIDER: Record<Dex, string> = {
  ZEROX: '0x',
  ONEINCH: '1inch',
  VELORA: 'Velora',
};

export function ControlloLiquidita({
  catena,
  letturaA,
  stantio,
  inCaricamento,
}: {
  catena: SaldiCatena | null;
  /** Momento della lettura on-chain: l'ora va accanto al numero, sempre. */
  letturaA: Date | null;
  stantio: boolean;
  inCaricamento: boolean;
}) {
  const [importo, setImporto] = useState('1000');
  const [vende, setVende] = useState('');
  const [compra, setCompra] = useState('');
  const [esito, setEsito] = useState<EsitoLiquidita | null>(null);
  const [conProfondita, setConProfondita] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /* I simboli vengono dai token che il wallet ha DAVVERO su questa catena: un
     elenco completo di token sarebbe un'altra ricerca, e qui interessa quello
     che si può muovere adesso. Il backend accetta ticker o indirizzo. */
  const simboli: VoceSelettore[] = catena
    ? [
        ...(catena.native ? [{ valore: catena.nativeSymbol, etichetta: `${catena.nativeSymbol} · moneta di gas` }] : []),
        ...(catena.tokens ?? []).map((t) => ({
          valore: t.symbol,
          etichetta: `${t.symbol} · ${importoToken(t.balance)}`,
        })),
      ]
    : [];

  const importoNumero = Number(importo.replace(',', '.'));
  const importoValido = Number.isFinite(importoNumero) && importoNumero > 0;
  const tokenVende = vende || simboli[0]?.valore || '';
  const tokenCompra = compra || simboli[1]?.valore || '';

  const motivoBlocco =
    catena == null
      ? 'Scegli prima una catena: il preventivo è per catena, non per wallet.'
      : !catena.ok
        ? `${catena.name} non risponde: senza lettura non si impegna capitale.`
        : simboli.length < 2
          ? 'Servono almeno due token su questa catena per chiedere un preventivo.'
          : !importoValido
            ? 'Scrivi un importo maggiore di zero.'
            : tokenVende === tokenCompra
              ? 'I due token devono essere diversi.'
              : inCorso
                ? 'Preventivo in corso…'
                : null;

  const gas = catena?.gasAlert;
  const statoVivo: StatoDatoVivo = inCaricamento
    ? 'caricamento'
    : catena == null || !catena.ok
      ? 'muto'
      : gas && (gas.level === 'empty' || gas.level === 'critical')
        ? 'insufficiente'
        : stantio
          ? 'stantio'
          : 'letto';

  async function chiedi(profondita: boolean) {
    if (motivoBlocco || !catena) return;
    setInCorso(true);
    setErrore(null);
    setConProfondita(profondita);
    try {
      const risposta = await tokens.liquidita({
        chainId: catena.chainId,
        sellToken: tokenVende,
        buyToken: tokenCompra,
        amountUsd: importoNumero,
        depthCurve: profondita,
      });
      setEsito(risposta);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Preventivo non riuscito.');
      setEsito(null);
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Scheda>
      <div style={{ fontWeight: 'var(--sb-peso-semi)', fontSize: 'var(--sb-t-14)' }}>
        Controllo di liquidità
      </div>
      <div className="sb-nota" style={{ marginTop: 'var(--sb-s-2)' }}>
        prima di impegnare capitale su una coppia DEX
      </div>

      <div className="sb-colonna" style={{ marginTop: 'var(--sb-s-6)' }}>
        <Selettore
          etichetta="Vendo"
          suggerimento={`${simboli.length} token nel wallet`}
          valore={tokenVende}
          voci={simboli}
          onCambia={setVende}
        />
        <Selettore
          etichetta="Compro"
          valore={tokenCompra}
          voci={simboli}
          onCambia={setCompra}
          errore={tokenVende === tokenCompra && simboli.length > 1 ? 'Scegli due token diversi.' : null}
        />

        <CampoDatoVivo
          etichetta="Capitale da impegnare"
          campo={
            <Campo
              etichetta="Importo"
              unita="USD"
              inputMode="decimal"
              value={importo}
              onChange={(e) => setImporto(e.target.value)}
            />
          }
          vivo={{
            valore: catena?.native
              ? `${importoToken(catena.native.balance)} ${catena.nativeSymbol}`
              : 'n/d',
            meta: metaGas(catena, letturaA, statoVivo),
            stato: statoVivo,
          }}
          azione={{
            etichetta: 'Chiedi il preventivo',
            onClick: () => chiedi(false),
            motivoBlocco,
          }}
        />
      </div>

      {gas && (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Avvertenza livello={gas.level === 'low' ? 'cautela' : 'grave'}>
            {gas.message} — coprono {gas.swapsCovered}{' '}
            {gas.swapsCovered === 1 ? 'operazione' : 'operazioni'}. Il preventivo si può chiedere
            comunque: non costa gas, ma lo scambio vero sì.
          </Avvertenza>
        </div>
      )}

      {errore && (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Avvertenza livello="grave">{errore}</Avvertenza>
        </div>
      )}

      {inCorso && (
        <div className="sb-riga" style={{ marginTop: 'var(--sb-s-6)' }}>
          <Rotella dim={14} />
          <span className="sb-nota">
            {conProfondita ? 'Fino a sette preventivi: può volerci qualche secondo…' : 'Preventivo in corso…'}
          </span>
        </div>
      )}

      {esito && !inCorso && (
        <Esito
          esito={esito}
          importoUsd={importoNumero}
          coppia={`${tokenVende} → ${tokenCompra}`}
          onProfondita={() => chiedi(true)}
        />
      )}
    </Scheda>
  );
}

function metaGas(catena: SaldiCatena | null, letturaA: Date | null, stato: StatoDatoVivo): string {
  if (stato === 'caricamento') return 'lettura del saldo di gas…';
  if (catena == null) return 'nessuna catena scelta';
  if (!catena.ok) return `${catena.name} non risponde${catena.error ? `: ${catena.error}` : ''}`;
  const quando = letturaA ? `letto alle ${ora(letturaA)} — ${daQuanto(letturaA)}` : 'ora di lettura sconosciuta';
  if (stato === 'insufficiente') return `gas insufficiente · ${quando}`;
  if (stato === 'stantio') return `non è di adesso · ${quando}`;
  return `saldo di gas su ${catena.name} · ${quando}`;
}

function Esito({
  esito,
  importoUsd,
  coppia,
  onProfondita,
}: {
  esito: EsitoLiquidita;
  importoUsd: number;
  coppia: string;
  onProfondita: () => void;
}) {
  const riferimento = esito.priceReference;
  const scarto = riferimento?.deltaCexVsDex;

  return (
    <div style={{ marginTop: 'var(--sb-s-7)' }}>
      <div className="sb-riga" style={{ gap: 'var(--sb-s-3)' }}>
        <span className="sb-occhiello">Preventivi reali</span>
        <span className="sb-nota">
          {coppia} · {valuta(importoUsd, '$', 0)}
        </span>
      </div>

      <Preventivo nome={NOME_PROVIDER.ZEROX} p={esito.zerox} consigliato={esito.recommendation === 'ZEROX'} />
      <Preventivo nome={NOME_PROVIDER.ONEINCH} p={esito.oneinch} consigliato={esito.recommendation === 'ONEINCH'} />
      <Preventivo nome={NOME_PROVIDER.VELORA} p={esito.velora} consigliato={esito.recommendation === 'VELORA'} />

      {esito.recommendation == null && (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Avvertenza livello="grave">
            Nessun provider ha dato un preventivo utilizzabile su questa size. Non è un problema di
            rete: è la coppia che non regge l'importo.
          </Avvertenza>
        </div>
      )}

      {esito.warnings?.map((a) => (
        <div key={a} style={{ marginTop: 'var(--sb-s-4)' }}>
          <Avvertenza livello="cautela">{a}</Avvertenza>
        </div>
      ))}

      {/* Il prezzo su cui il bot decide e il prezzo a cui eseguirebbe non sono
          lo stesso numero: se divergono, il segnale è già vecchio. */}
      {riferimento && (
        <div style={{ marginTop: 'var(--sb-s-6)' }}>
          <span className="sb-occhiello">Prezzi a confronto</span>
          {riferimento.marketWatcher && (
            <div className="sb-w-provider">
              <span className="sb-w-provider__nome">candela salvata</span>
              <span>
                {num(riferimento.marketWatcher.price, 4)} · {riferimento.marketWatcher.ageMinutes} min fa
              </span>
            </div>
          )}
          {riferimento.cexLive && (
            <div className="sb-w-provider">
              <span className="sb-w-provider__nome">CEX adesso</span>
              <span>{num(riferimento.cexLive.price, 4)}</span>
            </div>
          )}
          {riferimento.dexImplied && (
            <div className="sb-w-provider">
              <span className="sb-w-provider__nome">
                implicito dalla pool · {NOME_PROVIDER[riferimento.dexImplied.provider]}
              </span>
              <span>{num(riferimento.dexImplied.price, 4)}</span>
            </div>
          )}
          {scarto != null && (
            <div style={{ marginTop: 'var(--sb-s-4)' }}>
              <Avvertenza livello={Math.abs(scarto) > 1 ? 'cautela' : 'nota'}>
                Scarto fra CEX e DEX: {num(scarto, 2)}%
                {riferimento.deltaCexVsDexBasis
                  ? ` (base: prezzo ${riferimento.deltaCexVsDexBasis === 'live' ? 'live' : 'della candela'})`
                  : ''}
                . {Math.abs(scarto) > 1 ? 'Sopra il punto percentuale il segnale e l’esecuzione non parlano dello stesso prezzo.' : 'Entro il punto percentuale.'}
              </Avvertenza>
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 'var(--sb-s-7)' }}>
        {esito.depthCurve ? (
          <CurvaProfondita curva={esito.depthCurve} />
        ) : (
          <div className="sb-colonna" style={{ gap: 'var(--sb-s-3)' }}>
            <Pulsante variante="tenue" onClick={onProfondita}>
              Misura fin dove regge la coppia
            </Pulsante>
            <span className="sb-nota">
              costa fino a sette preventivi in più al solo provider consigliato: si chiede a mano,
              non a ogni modifica del campo
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function Preventivo({ nome, p, consigliato }: { nome: string; p: PreventivoDex; consigliato: boolean }) {
  return (
    <div className="sb-w-provider">
      <span className="sb-w-provider__nome">
        {nome}
        {consigliato && (
          <>
            {' '}
            <Distintivo tono="accento">consigliato</Distintivo>
          </>
        )}
      </span>
      <span>
        {p.available ? (
          <>
            {p.price != null ? num(p.price, 6) : '—'}
            {p.expectedSlippage != null && (
              <span className="sb-nota" style={{ display: 'block', marginTop: 4, textAlign: 'right' }}>
                slippage atteso {num(p.expectedSlippage, 2)}%
                {p.sources?.length ? ` · ${p.sources.slice(0, 2).join(', ')}` : ''}
              </span>
            )}
          </>
        ) : (
          <span className="sb-nota" style={{ color: 'var(--sb-cautela-testo)' }}>
            {p.error ?? 'nessun preventivo'}
          </span>
        )}
      </span>
    </div>
  );
}

function CurvaProfondita({ curva }: { curva: NonNullable<EsitoLiquidita['depthCurve']> }) {
  return (
    <div>
      <span className="sb-occhiello">Fin dove regge</span>
      <div style={{ marginTop: 'var(--sb-s-5)' }}>
        <GraficoProfondita
          punti={curva.points.map((p) => ({ amountUsd: p.amountUsd, impactPct: p.impactPct }))}
          sogliaPct={curva.thresholdPct}
        />
      </div>
      <div className="sb-w-legenda">
        <span>linea tratteggiata: soglia di impatto {num(curva.thresholdPct, 1)}%</span>
        <span>{curva.quotesExecuted} preventivi eseguiti</span>
      </div>

      <div className="sb-w-provider" style={{ marginTop: 'var(--sb-s-5)' }}>
        <span className="sb-w-provider__nome">utilizzabile fino a</span>
        <span>
          {curva.usableUpTo
            ? `${valuta(curva.usableUpTo.amountUsd, '$', 0)} · impatto ${num(curva.usableUpTo.impactPct, 2)}%`
            : 'nessuna size entro la soglia'}
        </span>
      </div>

      {curva.firstBreach && (
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <Avvertenza livello="cautela">
            Sfonda a {valuta(curva.firstBreach.amountUsd, '$', 0)}
            {curva.firstBreach.impactPct != null ? ` con impatto ${num(curva.firstBreach.impactPct, 2)}%` : ''} —{' '}
            {curva.firstBreach.reason === 'impact'
              ? 'per impatto sul prezzo'
              : curva.firstBreach.reason === 'liquidity'
                ? 'per liquidità insufficiente'
                : 'perché il provider non risponde più'}
            .
          </Avvertenza>
        </div>
      )}

      {curva.note && (
        <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
          {curva.note}
        </div>
      )}
    </div>
  );
}
